import type { ZodError } from 'zod';

export type LogType = 'info' | 'warn' | 'error' | 'validation';

export interface LogEntry {
  id: string;
  timestamp: string;
  type: LogType;
  message: string;
  details?: unknown;
}

type LogListener = (logs: LogEntry[]) => void;

class DebugLogger {
  private logs: LogEntry[] = [];
  private listeners: Set<LogListener> = new Set();
  private maxLogs = 300;

  private addLog(type: LogType, message: string, details?: unknown) {
    const globalObj = globalThis as unknown as Record<
      string,
      Record<string, Record<string, string>>
    >;
    const isTestEnv =
      typeof globalObj.process !== 'undefined' && globalObj.process.env?.NODE_ENV === 'test';

    const newEntry: LogEntry = {
      id: crypto.randomUUID(),
      timestamp: new Date().toLocaleTimeString(),
      type,
      message,
      details,
    };

    this.logs.unshift(newEntry);
    if (this.logs.length > this.maxLogs) {
      this.logs.pop();
    }

    this.notifyListeners();

    if (isTestEnv) {
      return;
    }

    // コンソールへのカラー出力
    const colors = {
      info: 'color: #00bfff; font-weight: bold;',
      warn: 'color: #ffaa00; font-weight: bold;',
      error: 'color: #ff3333; font-weight: bold;',
      validation: 'color: #ff33ff; font-weight: bold;',
    };

    console.log(
      `%c[${type.toUpperCase()}]%c ${message}`,
      colors[type],
      'color: inherit;',
      details !== undefined ? details : '',
    );
  }

  public info(message: string, details?: unknown) {
    this.addLog('info', message, details);
  }

  public warn(message: string, details?: unknown) {
    this.addLog('warn', message, details);
  }

  public error(message: string, details?: unknown) {
    this.addLog('error', message, details);
  }

  public validation(ncode: string, error: ZodError, rawData: unknown) {
    const errors = error.issues.map((e) => ({
      path: e.path.join('.'),
      message: e.message,
      code: e.code,
    }));
    this.addLog('validation', `N-Code: ${ncode} - Zod validation failed`, {
      errors,
      rawData,
    });
  }

  public getLogs(): LogEntry[] {
    return [...this.logs];
  }

  public clearLogs() {
    this.logs = [];
    this.notifyListeners();
  }

  public subscribe(listener: LogListener): () => void {
    this.listeners.add(listener);
    listener(this.getLogs());
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners() {
    for (const listener of this.listeners) {
      listener(this.getLogs());
    }
  }

  /**
   * ログを .log ファイルとしてブラウザでダウンロードする
   */
  public downloadLogs() {
    const content = this.logs
      .map(
        (log) =>
          `[${log.timestamp}] [${log.type.toUpperCase()}] ${log.message}\n${
            log.details ? `Details: ${JSON.stringify(log.details, null, 2)}\n` : ''
          }`,
      )
      .join('\n----------------------------------------\n\n');

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debug_logs_${new Date().toISOString().slice(0, 10)}.log`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

export const debugLogger = new DebugLogger();
